import React, { useState } from 'react';
import {
    Column,
    FileTile,
    FileTileDeleteButton,
    FileTileThumbnail,
    ResponsiveImage,
} from '@vp/swan';
import { StructuredBriefFile } from '@99designs/graph-utils/types';
import { __ } from '@99designs/i18n';
import { mapFileToFileKey, truncateFileName } from './mappers';

interface UploadedFileTileProps {
    file: StructuredBriefFile;
    onDelete: () => void;
    isDeleteAllowed: boolean;
}

export function UploadedFileTile({ file, onDelete, isDeleteAllowed }: UploadedFileTileProps) {
    const [isDeleting, setIsDeleting] = useState(false);
    return (
        <Column span={4} marginBottom="5">
            <FileTile
                skin={isDeleting ? 'queued' : undefined}
                fileName={truncateFileName(file.name)}
                fileDescription={
                    !(file.previewable && file.previewUrl) ? __('Preview unavailable') : undefined
                }
                accessibleTextForProgress={__('100% loaded')}
                accessibleTextForSpinner={isDeleting ? __('Deleting') : __('Waiting')}
            >
                <FileTileThumbnail>
                    {file.previewable && file.previewUrl ? (
                        <ResponsiveImage src={file.previewUrl} alt={file.name} />
                    ) : null}
                </FileTileThumbnail>
                {isDeleting || !isDeleteAllowed ? null : (
                    <FileTileDeleteButton
                        data-testid={`delete-${file.key}`}
                        accessibleText={__('Delete')}
                        onClick={() => {
                            setIsDeleting(true);
                            onDelete();
                        }}
                    />
                )}
            </FileTile>
        </Column>
    );
}

interface UploadingFileTileProps {
    file: File;
}

export function UploadingFileTile({ file }: UploadingFileTileProps) {
    return (
        <Column key={`uploading-${mapFileToFileKey(file)}`} span={4} marginBottom="5">
            <FileTile
                skin="queued"
                fileName={truncateFileName(file.name)}
                accessibleTextForProgress={__('0% loaded')}
                accessibleTextForSpinner={__('Waiting')}
            />
        </Column>
    );
}
